import { UI_CONFIG } from '@repperjs/data/src/firebase/ui-config-constants.ts';
import _d from '@repperjs/debug';
import FirebaseAuth from '@repperjs/shared/src/components/FirebaseAuth/FirebaseAuth.tsx';
import { getFirebaseStyles } from '@repperjs/shared/src/components/FirebaseAuth/firebaseui.styles.ts';
import { SESSION_API_ENDPOINT } from '@repperjs/shared/src/server/proxy/constants.ts';
import { callOnAppStart } from '@repperjs/shared/src/services/firebase/callOnAppStart.ts';
import { getAuth } from '@repperjs/shared/src/services/firebase/getAuth.ts';
import { useCallback, useState } from 'react';
import { GlobalStyles } from 'tss-react';
import Logo from '../../../assets/icons/logo/repper_logo_primary.svg';
import { useStyles } from './Login.styles.ts';
import { generateCsrfToken } from './utils/generateCsrfToken.ts';

const log = _d('repper:masterclass:Login'),
    error = _d('repper:error'),
    warn = _d('repper:warning');

type LoginState = 'init' | 'loading' | 'success' | 'error';

const firebaseAuth = getAuth();

function Login() {
    const { classes, theme } = useStyles();
    const firebaseStyles = getFirebaseStyles(theme);

    const [loginState, setLoginState] = useState<LoginState>('init');

    const confirmHandler = useCallback(
        (authResult: any) => {
            log('Login succeeded', authResult);

            const { user } = authResult;
            const setToken = async () => {
                // generate csrf token
                const csrfToken = generateCsrfToken();

                document.cookie = `csrfToken=${csrfToken}; path=/; secure; samesite=lax`;

                const idToken = await user.getIdToken();
                const resp = await fetch(SESSION_API_ENDPOINT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ idToken, csrfToken }),
                });

                if (!resp.ok) {
                    setLoginState('error');
                    error('Failed to exchange ID token for session cookie', await resp.text());
                    return;
                }

                try {
                    // must finish before the sign-out below, which drops the callable's auth
                    await callOnAppStart();
                } catch (e) {
                    error('onAppStart failed', e);
                }

                // Sign out of client-side Firebase (session cookie handles auth now)
                await firebaseAuth.signOut();

                // for now, redirect to home after login - ideally we should redirect back to where we came from
                window.location.href = '/';

                setLoginState('success');
            };
            setToken();
        },
        [firebaseAuth],
    );

    const failureHandler = useCallback((error: any) => {
        log('Login failed', error);
        setLoginState('error');
    }, []);

    const [authUILoaded, setAuthUILoaded] = useState(false);

    return (
        <>
            <GlobalStyles styles={firebaseStyles} />
            <div className={classes.root}>
                <div className={classes.loginContainer}>
                    <div className={classes.header}>
                        <div className={classes.logoContainer}>
                            <img src={Logo} className={classes.logo} alt="Repper Logo" />
                        </div>
                        <div className={classes.headerContainer}>Pattern Masterclass</div>
                        {loginState === 'init' && (
                            <>
                                <p>
                                    Learn to create patterns like a pro: principles and techniques.{' '}
                                    <a href="https://repper.app/masterclass/">Learn more</a>
                                </p>
                            </>
                        )}
                    </div>
                    <div className={classes.infoContainer}>
                        {loginState === 'error' && (
                            <>
                                <p>
                                    Failed to log in.
                                    <br />
                                    Please try again or{' '}
                                    <a href="mailto:support@repper.app?subject=Masterclass%20Login%20Issue">
                                        contact support
                                    </a>
                                    .
                                </p>
                                <a
                                    href="#"
                                    className={classes.reloadLink}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        window.location.reload();
                                    }}>
                                    Try again
                                </a>
                            </>
                        )}
                        {loginState === 'success' && (
                            <>
                                <p>
                                    You are logged in.
                                    <br />
                                    Wait to be redirected...
                                </p>
                                <span className={classes.loadingIndicator} />
                            </>
                        )}
                        {loginState === 'init' && (
                            <FirebaseAuth
                                uiConfig={{
                                    ...UI_CONFIG,
                                    callbacks: {
                                        signInSuccessWithAuthResult: confirmHandler,
                                        signInFailure: failureHandler,
                                        uiShown: () => setAuthUILoaded(true),
                                    },
                                }}
                                firebaseAuth={firebaseAuth}
                                className={classes.firebaseAuth}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default Login;
